import {
  Box,
  Flex,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RiAuctionLine } from "react-icons/ri";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import {
  AuctionProposalStatus,
  CreditAuctionProposalFilters,
} from "@/models/credit-auction-proposal.model";
import {
  INITIAL_REDIS_FILTERS,
  RedisSolicitacaoFilters,
} from "@/models/auction-redis.model";
import {
  HISTORICO_SOLICITACAO_STATUSES,
  HISTORICO_STATUS_LABELS,
  HistoricoSolicitacaoFilters,
  INITIAL_HISTORICO_FILTERS,
} from "@/models/auction-historico.model";
import CreditAuctionProposalsContent from "./credit-auction-proposals-content";
import CreditAuctionProgressContent from "./credit-auction-progress-content";
import CreditAuctionHistoricoContent from "./credit-auction-historico-content";
import CreditAuctionHistoricoExtraFilters from "./credit-auction-historico-extra-filters";
import CreditAuctionProposalsHeader from "./credit-auction-proposals-header";
import CreditAuctionProgressFilters from "./credit-auction-progress-filters";

const TAB_STYLE = {
  fontFamily: "Lexend Deca",
  fontWeight: "bold",
  fontSize: { base: "lg", md: "xl", lg: "2xl" },
  color: "whiteAlpha.800",
  _selected: { color: "white", borderBottomColor: "white" },
} as const;

const MAX_MARGIN_RANGE = 50000;
const MIN_MARGIN_RANGE = 500;

const formatDateToApi = (date: Date | null): string => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const criarFiltrosIniciaisLeilao = (): CreditAuctionProposalFilters => {
  const hoje = formatDateToApi(new Date());
  return {
    statuses: [],
    wonAuction: null,
    startDate: hoje,
    endDate: hoje,
    validadeStartDate: "",
    validadeEndDate: "",
    minReleasedValue: "500",
    maxReleasedValue: "50000",
    negotiationSubStep: null,
  };
};

export default function CreditAuctionProposalsPage() {
  const backgroundStyle = useConditionalBackground();
  const queryClient = useQueryClient();
  const [tabIndex, setTabIndex] = useState<number>(1);

  // --- Estado da aba "Leilão interno" ---
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const [filters, setFilters] = useState<CreditAuctionProposalFilters>(
    criarFiltrosIniciaisLeilao,
  );
  const [appliedFilters, setAppliedFilters] =
    useState<CreditAuctionProposalFilters>(criarFiltrosIniciaisLeilao);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    () => [new Date(), new Date()],
  );
  const [validadeDateRange, setValidadeDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [applyCounter, setApplyCounter] = useState<number>(0);
  const [auctionPage, setAuctionPage] = useState<number>(1);
  const [auctionPageSize, setAuctionPageSize] = useState<number>(10);
  const [auctionTotalItems, setAuctionTotalItems] = useState<number>(0);

  // --- Estado da aba "Progresso leilão" (Redis) ---
  const [progressFilters, setProgressFilters] =
    useState<RedisSolicitacaoFilters>(INITIAL_REDIS_FILTERS);
  const [appliedProgressFilters, setAppliedProgressFilters] =
    useState<RedisSolicitacaoFilters>(INITIAL_REDIS_FILTERS);
  const [progressPage, setProgressPage] = useState<number>(1);
  const [progressPageSize, setProgressPageSize] = useState<number>(10);

  // --- Estado da aba "Histórico leilão" (tabela leilao_solicitacao_historico) ---
  const [historicoFilters, setHistoricoFilters] =
    useState<HistoricoSolicitacaoFilters>(INITIAL_HISTORICO_FILTERS);
  const [appliedHistoricoFilters, setAppliedHistoricoFilters] =
    useState<HistoricoSolicitacaoFilters>(INITIAL_HISTORICO_FILTERS);
  const [historicoPage, setHistoricoPage] = useState<number>(1);
  const [historicoPageSize, setHistoricoPageSize] = useState<number>(10);

  // --- Handlers da aba "Leilão interno" ---
  const handleAuctionFilterChange = (
    field: keyof CreditAuctionProposalFilters,
    value: string | boolean | null,
  ): void => {
    setFilters((previous) => ({
      ...previous,
      [field]:
        field === "statuses"
          ? value
            ? [value as AuctionProposalStatus]
            : []
          : value,
    }));
  };

  const handleDateRangeChange = (
    dates: [Date | null, Date | null],
  ): void => {
    setDateRange(dates);
    setFilters((previous) => ({
      ...previous,
      startDate: formatDateToApi(dates[0]),
      endDate: formatDateToApi(dates[1]),
    }));
  };

  const handleValidadeDateRangeChange = (
    dates: [Date | null, Date | null],
  ): void => {
    setValidadeDateRange(dates);
    setFilters((previous) => ({
      ...previous,
      validadeStartDate: formatDateToApi(dates[0]),
      validadeEndDate: formatDateToApi(dates[1]),
    }));
  };

  const handleReleasedValueRangeChange = (range: {
    min: number;
    max: number;
  }): void => {
    setFilters((previous) => ({
      ...previous,
      minReleasedValue: String(range.min),
      maxReleasedValue: String(range.max),
    }));
  };

  const handleApplyAuctionFilters = (): void => {
    setAuctionPage(1);
    setAppliedFilters(filters);
    setAppliedSearchTerm(searchTerm);
    setApplyCounter((previous) => previous + 1);
  };

  const handleClearAuctionFilters = (): void => {
    const filtrosIniciais = criarFiltrosIniciaisLeilao();
    setFilters(filtrosIniciais);
    setAppliedFilters(filtrosIniciais);
    setDateRange([new Date(), new Date()]);
    setValidadeDateRange([null, null]);
    setSearchTerm("");
    setAppliedSearchTerm("");
    setAuctionPage(1);
    setApplyCounter((previous) => previous + 1);
  };

  // --- Handlers da aba "Progresso leilão" ---
  const handleApplyProgressFilters = (): void => {
    setProgressPage(1);
    setAppliedProgressFilters(progressFilters);
  };

  const handleClearProgressFilters = (): void => {
    setProgressFilters(INITIAL_REDIS_FILTERS);
    setAppliedProgressFilters(INITIAL_REDIS_FILTERS);
    setProgressPage(1);
  };

  // --- Handlers da aba "Histórico leilão" ---
  const handleApplyHistoricoFilters = (): void => {
    setHistoricoPage(1);
    setAppliedHistoricoFilters(historicoFilters);
  };

  const handleClearHistoricoFilters = (): void => {
    setHistoricoFilters(INITIAL_HISTORICO_FILTERS);
    setAppliedHistoricoFilters(INITIAL_HISTORICO_FILTERS);
    setHistoricoPage(1);
  };

  return (
    <Tabs
      colorScheme="teal"
      width="100%"
      isLazy
      index={tabIndex}
      onChange={setTabIndex}
    >
      <Box
        padding={{ base: 2, md: 4 }}
        display="flex"
        flexDirection="column"
        width="100%"
      >
        <Box
          {...backgroundStyle}
          borderRadius="md"
          p={{ base: 4, md: 6 }}
          color="white"
          boxShadow="md"
          mb={4}
        >
          <Flex
            flexDirection={{ base: "column", md: "row" }}
            alignItems={{ base: "flex-start", md: "center" }}
            justifyContent="space-between"
            mb={4}
          >
            <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
              <Flex
                alignItems="center"
                justifyContent="center"
                w="54px"
                h="54px"
                borderRadius="lg"
                bg="whiteAlpha.200"
                mr={4}
              >
                <Icon as={RiAuctionLine} boxSize={8} />
              </Flex>
              <Text
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="bold"
                fontFamily="Lexend Deca"
              >
                Leilão CLT
              </Text>
              <TabList ml={6} borderBottom="none">
                <Tab {...TAB_STYLE}>Leilão BMP</Tab>
                <Tab {...TAB_STYLE}>Progresso leilão</Tab>
                <Tab {...TAB_STYLE}>Histórico leilão</Tab>
              </TabList>
            </Flex>
          </Flex>

          {tabIndex === 0 && (
            <CreditAuctionProgressFilters
              filters={progressFilters}
              onChange={setProgressFilters}
              onApply={handleApplyProgressFilters}
              onClear={handleClearProgressFilters}
              onRefresh={() =>
                queryClient.invalidateQueries({
                  queryKey: ["auction-redis-solicitacoes"],
                })
              }
            />
          )}

          {tabIndex === 2 && (
            <CreditAuctionProgressFilters
              filters={historicoFilters}
              onChange={setHistoricoFilters}
              onApply={handleApplyHistoricoFilters}
              onClear={handleClearHistoricoFilters}
              statusOptions={HISTORICO_SOLICITACAO_STATUSES}
              statusLabels={HISTORICO_STATUS_LABELS}
              searchPlaceholder="CPF completo ou nº da solicitação"
              extraFilters={
                <CreditAuctionHistoricoExtraFilters
                  filters={historicoFilters}
                  onChange={setHistoricoFilters}
                />
              }
            />
          )}

          {tabIndex === 1 && (
            <CreditAuctionProposalsHeader
              embedded
              minRangeLimit={MIN_MARGIN_RANGE}
              maxRangeLimit={MAX_MARGIN_RANGE}
              searchTerm={searchTerm}
              filters={filters}
              dateRange={dateRange}
              validadeDateRange={validadeDateRange}
              totalItems={auctionTotalItems}
              onSearchChange={setSearchTerm}
              onFilterChange={handleAuctionFilterChange}
              onDateRangeChange={handleDateRangeChange}
              onValidadeDateRangeChange={handleValidadeDateRangeChange}
              onReleasedValueRangeChange={handleReleasedValueRangeChange}
              onClearFilters={handleClearAuctionFilters}
              onApplyFilters={handleApplyAuctionFilters}
            />
          )}
        </Box>

        <TabPanels>
          <TabPanel px={0} py={0}>
            <CreditAuctionProgressContent
              appliedFilters={appliedProgressFilters}
              page={progressPage}
              pageSize={progressPageSize}
              onPageChange={setProgressPage}
              onPageSizeChange={(size) => {
                setProgressPageSize(size);
                setProgressPage(1);
              }}
            />
          </TabPanel>
          <TabPanel px={0} py={0}>
            <CreditAuctionProposalsContent
              appliedSearchTerm={appliedSearchTerm}
              appliedFilters={appliedFilters}
              applyCounter={applyCounter}
              page={auctionPage}
              pageSize={auctionPageSize}
              onPageChange={setAuctionPage}
              onPageSizeChange={(size) => {
                setAuctionPageSize(size);
                setAuctionPage(1);
              }}
              minMarginRange={MIN_MARGIN_RANGE}
              maxMarginRange={MAX_MARGIN_RANGE}
              onTotalItemsChange={setAuctionTotalItems}
            />
          </TabPanel>
          <TabPanel px={0} py={0}>
            <CreditAuctionHistoricoContent
              appliedFilters={appliedHistoricoFilters}
              page={historicoPage}
              pageSize={historicoPageSize}
              onPageChange={setHistoricoPage}
              onPageSizeChange={(size) => {
                setHistoricoPageSize(size);
                setHistoricoPage(1);
              }}
            />
          </TabPanel>
        </TabPanels>
      </Box>
    </Tabs>
  );
}
